import Client from '@spike/client-model';
import { MetricBox } from 'components/UI/V3/MetricBox/MetricBox';
import { FunctionComponent, ReactNode } from 'react';
import LeadActivitySection from './Activity/LeadActivitySection';
import LeadStagesCard from './Stages/LeadStagesCard';
import { buildLeadAge, buildLeadDateMetrics, EMPTY_METRIC, LeadDateTime } from './leadMetrics';
import styles from './LeadOverviewTab.module.scss';

interface LeadOverviewTabProps {
    lead: Client;
    timeZone: string;
    activityRefreshSignal?: number;
}

/** V3 `MetricBox` renders `value` inside a `<p>`, so date metrics stack in a `<span>` subtree. */
const renderMetricValue = (value: string | LeadDateTime | undefined): ReactNode => {
    if (value === undefined) {
        return <span className={styles.metricValue}>{EMPTY_METRIC}</span>;
    }

    if (typeof value === 'string') {
        return value;
    }

    return (
        <span className={styles.metricDateTime}>
            <span className={styles.metricValue}>{value.date}</span>
            <span className={styles.metricTime}>{value.time}</span>
        </span>
    );
};

const LeadOverviewTab: FunctionComponent<LeadOverviewTabProps> = ({ lead, timeZone, activityRefreshSignal }) => (
    <div className={styles.root}>
        <LeadStagesCard
            lead={lead}
            timeZone={timeZone}
        />

        <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Overview</h3>

            <div className={styles.metrics}>
                <MetricBox
                    className={styles.metricBox}
                    title="Lead age"
                    value={<span className={styles.metricAge}>{buildLeadAge(lead, timeZone)}</span>}
                />
                {buildLeadDateMetrics(lead, timeZone).map(metric => (
                    <MetricBox
                        key={metric.label}
                        className={styles.metricBox}
                        title={metric.label}
                        value={renderMetricValue(metric.value)}
                    />
                ))}
            </div>
        </div>

        {lead.id !== undefined && (
            <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Activity</h3>

                <LeadActivitySection
                    leadId={lead.id}
                    timeZone={timeZone}
                    refreshSignal={activityRefreshSignal}
                />
            </div>
        )}
    </div>
);

export default LeadOverviewTab;
